// Company: the business the personas are trying to keep afloat — cash, KPIs,
// objectives, the jobs board, recent trips and happenings. Admins also get
// the control panel (kill switch, subsystems, spend).
import { Link } from 'wouter';
import { api } from '../../convex/_generated/api';
import type { Doc } from '../../convex/_generated/dataModel';
import { placeByKey } from '../../convex/shared/scenario';
import type { VehicleView } from '../../convex/shared/views';
import { ControlPanel } from '../components/ControlPanel';
import { fmtClock, fmtDayTime, fmtPct, fmtRelative, fmtUsd, humanize, shortName } from '../lib/format';
import { useCast, useNow, type Cast } from '../lib/hooks';
import { useSession, useSimQuery } from '../lib/session';
import { Avatar } from '../ui/Avatar';
import { Icon } from '../ui/Icon';
import { Chip, Empty, Meter, Panel, Spinner, Stat } from '../ui/primitives';

type Job = Doc<'jobs'>;
const COLUMNS: { status: Job['status'][]; title: string; tone: string }[] = [
  { status: ['open'], title: 'Open', tone: 'sky' },
  { status: ['assigned'], title: 'Assigned', tone: 'grape' },
  { status: ['en_route', 'on_site'], title: 'In progress', tone: 'sun' },
  { status: ['late'], title: 'Late', tone: 'coral' },
  { status: ['done'], title: 'Done', tone: 'leaf' },
];

export function CompanyView() {
  const { isAdmin } = useSession();
  const now = useNow(30_000);
  const cast = useCast();
  const company = useSimQuery(api.company.sim.getCompany, {});
  const jobs = useSimQuery(api.company.jobs.listJobs, { limit: 80 });
  const events = useSimQuery(api.company.sim.listCompanyEvents, { limit: 30 });
  const trips: Doc<'trips'>[] | undefined = useSimQuery(api.world.trips.listRecentTrips, { limit: 20 });
  const vehicles: VehicleView[] | undefined = useSimQuery(api.world.vehicles.listVehicles, {});

  const onTime = company ? (company.kpis.onTimePct > 1 ? company.kpis.onTimePct / 100 : company.kpis.onTimePct) : 0;
  const vehicleName = (id: string) => vehicles?.find((v) => v._id === id)?.name ?? 'A vehicle';

  return (
    <div className="page">
      <header className="page-head">
        <h1>{company?.name ?? 'The company'}</h1>
        <p>Cash, deadlines and safety are the pressures everyone feels. Nothing here is scripted: it is the sum of what the personas decide.</p>
      </header>

      {company === undefined ? <Spinner /> : null}
      {company === null ? <Empty icon="building" title="The company has not opened yet">Seed the scenario to open the doors.</Empty> : null}
      {company ? (
        <>
          <div className="stat-row">
            <Stat icon="dollar" tone="leaf" label="Cash" value={fmtUsd(company.cash)} hint={`Week: +${fmtUsd(company.weekRevenue)} / −${fmtUsd(company.weekCosts)}`} />
            <Stat icon="shield" tone="sky" label="Safety score" value={Math.round(company.kpis.safetyScore)} hint={`${company.kpis.incidentsWeek} incidents this week`} />
            <Stat icon="clock" tone="sun" label="On time" value={fmtPct(onTime)} hint={`${company.kpis.jobsDoneToday} done · ${company.kpis.jobsLateToday} late today`} />
            <Stat icon="flag" tone="coral" label="Clean days" value={company.kpis.daysSincePreventableIncident} hint="since a preventable incident" />
            <Stat icon="fuel" tone="grape" label="Fuel this week" value={fmtUsd(company.kpis.fuelSpendWeek)} />
            <Stat icon="sparkle" tone="leaf" label="Customers" value={`${company.kpis.customerSat.toFixed(1)} / 5`} hint={`Morale ${fmtPct(company.morale)}`} />
          </div>
          <Panel title="Objectives" icon="target">
            <div className="objectives">
              {company.objectives.map((o) => (
                <Meter key={o.key} label={o.text} detail={fmtPct(o.progress)} value={o.progress} tone={o.progress >= 1 ? 'leaf' : 'sky'} />
              ))}
            </div>
          </Panel>
        </>
      ) : null}

      {isAdmin ? <ControlPanel /> : null}

      <Panel title="Jobs board" icon="list">
        {jobs === undefined ? <Spinner /> : null}
        {jobs?.length === 0 ? <Empty icon="list" title="No jobs today">Jobs are generated each morning at 05:00 Austin time.</Empty> : null}
        {jobs && jobs.length > 0 ? (
          <div className="board">
            {COLUMNS.map((col) => {
              const items = jobs.filter((j) => col.status.includes(j.status));
              return (
                <div key={col.title} className={`board-col tone-${col.tone}`}>
                  <h3>
                    {col.title} <span>{items.length}</span>
                  </h3>
                  {items.map((j) => (
                    <JobCard key={j._id} job={j} cast={cast} />
                  ))}
                </div>
              );
            })}
          </div>
        ) : null}
      </Panel>

      <div className="two-col">
        <Panel title="Recent trips" icon="truck">
          {trips === undefined ? <Spinner /> : null}
          {trips?.length === 0 ? <Empty icon="truck" title="Nobody has driven yet" /> : null}
          <ul className="trip-list">
            {trips?.map((t) => (
              <li key={t._id} className={`trip status-${t.status}`}>
                <div className="trip-main">
                  <b>{vehicleName(t.vehicleId)}</b>
                  <span>
                    {humanize(t.purpose)} → {t.destination.label}
                  </span>
                </div>
                <div className="trip-meta">
                  {t.driver.kind === 'persona' ? <span>{cast?.nameOf(t.driver.personaId)}</span> : t.driver.kind === 'human' ? <span>The owner</span> : null}
                  <span>{fmtDayTime(t.startedAt ?? t.departAt)}</span>
                  <Chip tone={t.status === 'active' ? 'sun' : t.status === 'aborted' ? 'danger' : t.status === 'completed' ? 'leaf' : 'neutral'}>{humanize(t.status)}</Chip>
                  {t.nfcTapped ? <Chip icon="nfc" tone="grape">Tapped in</Chip> : null}
                  {t.hitchedAssetIds.length > 0 ? <Chip icon="trailer">Towing</Chip> : null}
                </div>
                {t.outcome ? <p className="muted">{t.outcome}</p> : null}
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Happenings" icon="building">
          {events === undefined ? <Spinner /> : null}
          {events?.length === 0 ? <Empty icon="building" title="A quiet day" /> : null}
          <ol className="events">
            {events?.map((e) => (
              <li key={e._id} className={`event severity-${e.severity}`}>
                <time>{fmtClock(e.at)}</time>
                <span className="event-kind">{humanize(e.kind)}</span>
                <p>{e.text}</p>
                <span className="event-people">
                  {e.personaIds.map((id) => {
                    const p = cast?.byId.get(id);
                    return p ? (
                      <Link key={id} href={`/agent/${p.key}`} title={p.name}>
                        <Avatar personaKey={p.key} mood={p.mood} size={20} />
                      </Link>
                    ) : null;
                  })}
                </span>
                <span className="muted">{fmtRelative(e.at, now)}</span>
              </li>
            ))}
          </ol>
        </Panel>
      </div>
    </div>
  );
}

function JobCard({ job: j, cast }: { job: Job; cast: Cast | undefined }) {
  const assignee = j.assignedPersonaId ? cast?.byId.get(j.assignedPersonaId) : undefined;
  const place = placeByKey(j.placeKey);
  return (
    <article className={`job priority-${j.priority}`}>
      <header>
        <code>{j.code}</code>
        {j.priority === 'emergency' || j.priority === 'high' ? <Chip tone={j.priority === 'emergency' ? 'danger' : 'sun'}>{humanize(j.priority)}</Chip> : null}
      </header>
      <strong>{j.customerName}</strong>
      <p>{j.description}</p>
      <div className="job-facts">
        <span>
          <Icon name="pin" size={14} /> {place?.name ?? j.placeKey}
        </span>
        <span>
          <Icon name="clock" size={14} /> {fmtClock(j.windowStart)}–{fmtClock(j.windowEnd)}
        </span>
        <span>
          <Icon name="dollar" size={14} /> {fmtUsd(j.revenue)}
        </span>
      </div>
      <div className="job-chips">
        <Chip>{humanize(j.kind)}</Chip>
        {j.requiresAssetKind ? <Chip icon="trailer">Needs {humanize(j.requiresAssetKind).toLowerCase()}</Chip> : null}
        {j.scheduledInApp ? (
          <Chip tone="leaf" icon="clock">
            Scheduled trip in aa-app
          </Chip>
        ) : null}
      </div>
      {assignee ? (
        <footer>
          <Avatar personaKey={assignee.key} mood={assignee.mood} size={22} /> {shortName(assignee.name)}
        </footer>
      ) : null}
    </article>
  );
}
